/**
 * Downloads — pull any report, for any client, window and shape, without waiting for a
 * schedule to fire.
 *
 * The request that actually arrives is "can I get Fusion's numbers for the 9th as well,
 * and only the calls that have audio". So this screen is deliberately not a view of the
 * schedules: it is the same dispatcher, the same column policy and the same right-party
 * gate, with `deliver` false — the file is produced and handed back through a grant
 * instead of being mailed to the client's recipients. Nobody's inbox gets a copy of an
 * internal query.
 *
 * Three things this screen must never let someone get wrong:
 *
 *  1. **The detailed shape over a long window.** One row per call; a month-to-date
 *     detailed report is ~28M rows. The dispatcher refuses it (CONTRACT §3), so the form
 *     refuses it first rather than queueing a run that can only fail.
 *  2. **Opt-in content.** Anything derived from what was said on the call is opt-in
 *     regardless of size (CONTRACT §6), and it is suppressed again at emit time on rows
 *     where the borrower was not the counterparty. Never-tier columns are shown as
 *     unreachable, not hidden — an operator who cannot see why a column is missing files
 *     a ticket.
 *  3. **What they actually got.** XLSX silently becoming gzipped CSV is the single most
 *     confusing thing this pipeline does, so every artifact states its format, its row
 *     count and its size, and a fallback says so in words with the reason.
 *  4. **How much of the day is in it.** A partial source is ACCEPTED here, because
 *     nothing leaves the building through this screen — but files pulled here get
 *     forwarded by hand, and an unlabelled part-day forwarded to a client is the exact
 *     defect CONTRACT-COVERAGE exists to prevent. So the coverage sits beside the
 *     Download button, not in a drawer two clicks away.
 *
 * Downloading is itself a grant (CONTRACT §1): the console never presigns an object. It
 * mints a token with the same 7-day window, the same revoke switch and the same per-click
 * audit trail a client's link has.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { Card, PageHeader } from '@/components/Layout';
import Button from '@/components/Button';
import EmptyState from '@/components/EmptyState';
import GrantList, { copyText, mintGrant, mintedUrl } from '@/components/GrantList';
import Table, { type Column } from '@/components/Table';
import { Badge, CoverageBadge, PolicyBadge, StatusBadge, MisalignedBadge } from '@/components/Badge';
import { Checkbox, Field, Input, Select, Switch } from '@/components/Field';
import { Pagination, useCursorPagination } from '@/components/Pagination';
import { useToast } from '@/components/Toast';
import { startDownload } from '@/lib/download';
import { api, errorMessage, isApiError } from '@/lib/api';
import {
  basename,
  coverageOf,
  coverageWindowLabel,
  describeWindowResolved,
  formatBytes,
  formatCount,
  istDateRange,
  istDateTime,
  istToday,
  resolveWindow,
  timeAgo,
  misalignedNote,
} from '@/lib/format';
import {
  DETAILED_MAX_WINDOW_DAYS,
  REPORT_SHAPES,
  RUN_IN_FLIGHT,
  WINDOW_TYPES,
  type Client,
  type DownloadCreate,
  type DownloadItem,
  type IsoDate,
  type Page,
  type ReportFormat,
  type ReportShape,
  type ReportWindow,
  type Run,
  type RunArtifact,
  type RunStatus,
  type WindowType,
} from '@/lib/types';

// ---------------------------------------------------------------------------
// Format facts — these mirror services/dispatcher/xlsx.py exactly
// ---------------------------------------------------------------------------

/**
 * Field rows on the generate form. `.field-row` is `auto-fit, minmax(200px, 1fr)`, which
 * handed a single 7-option Select the whole card width (~1,140px) and left 239px of dead
 * space beside the hour/minute pickers. Capping the track and packing left collapses the
 * three rows toward two without shrinking any control below its comfortable size.
 */
const CAPPED_FIELDS: CSSProperties = {
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 280px))',
  justifyContent: 'start',
};

/** Above this many rows the writer does not even attempt a workbook. */
const XLSX_ROW_THRESHOLD = 800_000;
/** A worksheet cannot hold more than this many rows, header included. */
const XLSX_SHEET_CEILING = 1_048_576;

/** Extra fields the dispatcher records on each artifact, beyond the contract's four. */
interface ArtifactRow extends RunArtifact {
  /** 'xlsx' or 'csv.gz' — what was actually written, which is not always what was asked for. */
  format?: string;
  filename?: string;
  label?: string;
  /** Column count, not the names. */
  columns?: number;
}

/** What GET /api/downloads returns, including the denormalised extras. */
interface DownloadRow extends DownloadItem {
  artifacts: ArtifactRow[];
  status?: RunStatus;
  format?: ReportFormat;
  window_label?: string;
  delivered?: boolean;
}

function formatLabel(fmt: string | undefined): string {
  if (fmt === 'csv.gz') return 'CSV.GZ';
  if (fmt === 'csv') return 'CSV';
  if (fmt === 'xlsx') return 'XLSX';
  return (fmt || 'unknown').toUpperCase();
}

/** True when XLSX was requested and gzipped CSV came out. Asking for CSV is not a fallback. */
function fellBack(artifact: ArtifactRow, requested: string | undefined): boolean {
  const written = artifact.format ?? requested;
  return requested === 'xlsx' && written === 'csv.gz';
}

/**
 * Why it fell back. The row count is the reason in every case we can prove from here;
 * below the threshold the writer itself failed, and we say that rather than invent a
 * number. The dispatcher log for the run carries the exact exception.
 */
function fallbackReason(artifact: ArtifactRow): string {
  if (artifact.rows > XLSX_ROW_THRESHOLD) {
    return `${formatCount(artifact.rows)} rows is over the ${formatCount(
      XLSX_ROW_THRESHOLD,
    )}-row XLSX threshold (one worksheet stops at ${formatCount(
      XLSX_SHEET_CEILING,
    )}), so the rows were written as gzipped CSV instead of being truncated.`;
  }
  return 'The workbook write did not complete — the runtime had no XLSX writer available, or a row/column limit was hit mid-write — so the rows were spilled to gzipped CSV rather than truncated. The dispatcher log for this run names the exact cause.';
}

function artifactCount(item: DownloadRow): number {
  return (item.artifacts || []).length;
}

function anyFallback(item: DownloadRow): boolean {
  return (item.artifacts || []).some((a) => fellBack(a, item.format));
}

// ---------------------------------------------------------------------------
// Window form state
// ---------------------------------------------------------------------------

interface WindowDraft {
  type: WindowType;
  n: number;
  from: IsoDate;
  to: IsoDate;
}

function buildWindow(draft: WindowDraft): { window: ReportWindow | null; error: string | null } {
  switch (draft.type) {
    case 'same_day':
      return { window: { type: 'same_day' }, error: null };
    case 'yesterday':
      return { window: { type: 'yesterday' }, error: null };
    case 'mtd':
      return { window: { type: 'mtd' }, error: null };
    case 'last_n_days': {
      if (!Number.isFinite(draft.n) || draft.n < 1) {
        return { window: null, error: 'Give a whole number of days, 1 or more.' };
      }
      if (draft.n > 366) return { window: null, error: 'Cap a last-N window at 366 days.' };
      return { window: { type: 'last_n_days', n: Math.floor(draft.n) }, error: null };
    }
    case 'from_date': {
      if (!draft.from) return { window: null, error: 'Pick the start date.' };
      if (draft.from > istToday()) {
        return { window: null, error: 'The start date is in the future (IST).' };
      }
      return { window: { type: 'from_date', from: draft.from }, error: null };
    }
    case 'custom': {
      if (!draft.from || !draft.to) return { window: null, error: 'Pick both dates.' };
      if (draft.from > draft.to) {
        return { window: null, error: 'The start date is after the end date.' };
      }
      return { window: { type: 'custom', from: draft.from, to: draft.to }, error: null };
    }
    default:
      return { window: null, error: 'Unknown window type.' };
  }
}

// ---------------------------------------------------------------------------
// Queued work
// ---------------------------------------------------------------------------

interface Queued {
  run_id: string;
  client_id: string;
  shape: ReportShape;
  audio_only: boolean;
  status: RunStatus;
  requested_at: string;
  error?: string | null;
  rows_out?: number;
}

const POLL_MS = 4000;

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function Downloads() {
  const toast = useToast();

  // --- clients -------------------------------------------------------------
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsError, setClientsError] = useState<unknown>(null);
  const [clientsLoading, setClientsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setClientsLoading(true);
    api.clients
      .listAll()
      .then((items) => {
        if (cancelled) return;
        setClients(items);
        setClientsError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setClientsError(err);
      })
      .finally(() => {
        if (!cancelled) setClientsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const clientById = useMemo(() => {
    const map = new Map<string, Client>();
    for (const c of clients) map.set(c.client_id, c);
    return map;
  }, [clients]);

  // --- generate form -------------------------------------------------------
  const [clientId, setClientId] = useState('');
  const [shape, setShape] = useState<ReportShape>('borrower');
  const [draft, setDraft] = useState<WindowDraft>({
    type: 'yesterday',
    n: 7,
    from: istToday(),
    to: istToday(),
  });
  const [format, setFormat] = useState<ReportFormat>('xlsx');
  const [audioOnly, setAudioOnly] = useState(false);
  const [flow, setFlow] = useState('');
  const [campaign, setCampaign] = useState('');
  const [optIn, setOptIn] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [queued, setQueued] = useState<Queued[]>([]);

  // Default to the first active client once the list lands.
  useEffect(() => {
    if (clientId || clients.length === 0) return;
    const first = clients.find((c) => c.active) ?? clients[0];
    if (first) setClientId(first.client_id);
  }, [clients, clientId]);

  const client = clientId ? clientById.get(clientId) : undefined;

  // A different client means a different schema: opt-in selections cannot carry over.
  useEffect(() => {
    setOptIn([]);
  }, [clientId]);

  const optInColumns = useMemo(() => client?.column_policy?.opt_in ?? [], [client]);
  const neverColumns = useMemo(() => client?.column_policy?.never ?? [], [client]);

  const recordingColumn = client?.field_map?.recording;
  const recordingIsNever = Boolean(
    recordingColumn &&
      neverColumns.some((c) => c.toLowerCase() === String(recordingColumn).toLowerCase()),
  );
  const audioBlockedReason = !client
    ? 'Pick a client first.'
    : !recordingColumn
      ? `${client.name} has no recording column in its field_map, so a recordings index would have no rows.`
      : recordingIsNever
        ? `${recordingColumn} is a never-tier column for this client and can never be released.`
        : null;

  // Audio-only is meaningless without a recording column; do not leave it silently on.
  useEffect(() => {
    if (audioBlockedReason) setAudioOnly(false);
  }, [audioBlockedReason]);

  const built = useMemo(() => buildWindow(draft), [draft]);
  const resolved = useMemo(
    () => (built.window ? resolveWindow(built.window) : null),
    [built.window],
  );

  const guardrail =
    shape === 'detailed' && resolved && resolved.days > DETAILED_MAX_WINDOW_DAYS
      ? `A detailed report is one row per call. ${resolved.days} days is over the ${DETAILED_MAX_WINDOW_DAYS}-day limit and the dispatcher refuses it — a month of detail is roughly 28 million rows. Use the borrower shape for this window, or narrow the dates.`
      : null;

  const canSubmit = Boolean(client && built.window && !guardrail && !submitting);

  const toggleOptIn = (column: string) => {
    setOptIn((current) =>
      current.includes(column) ? current.filter((c) => c !== column) : [...current, column],
    );
  };

  const submit = async () => {
    if (!client || !built.window || guardrail) return;
    const body: DownloadCreate = {
      client_id: client.client_id,
      shape,
      window: built.window,
      include_opt_in: optIn,
      flow: flow.trim() || null,
      campaign_pattern: campaign.trim() || null,
      format,
      audio_only: audioOnly,
    };

    setSubmitting(true);
    try {
      const result = await api.downloads.create(body);
      setQueued((current) => [
        {
          run_id: result.run_id,
          client_id: client.client_id,
          shape,
          audio_only: audioOnly,
          status: result.status ?? 'resolving',
          requested_at: new Date().toISOString(),
        },
        ...current.filter((q) => q.run_id !== result.run_id),
      ].slice(0, 8));
      toast.success('Generation queued', {
        description: `${client.name} · ${describeWindowResolved(built.window)}. It appears below when it finishes.`,
      });
    } catch (err) {
      if (isApiError(err, 'never_column')) {
        toast.error('That column can never be released', { description: errorMessage(err) });
      } else if (isApiError(err, 'no_recording_column')) {
        toast.error('No recording column mapped', { description: errorMessage(err) });
      } else {
        toast.error(errorMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // --- listing -------------------------------------------------------------
  // 50, the same as every other list in the console. 25 made this the only screen that
  // paged twice as often, for no stated reason.
  const pager = useCursorPagination(50);
  const [filterClient, setFilterClient] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterShape, setFilterShape] = useState<'' | ReportShape>('');
  const [filterAudio, setFilterAudio] = useState<'' | 'only' | 'exclude'>('');
  const [page, setPage] = useState<Page<DownloadRow>>({ items: [], cursor: null });
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<unknown>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const filtersActive = Boolean(
    filterClient || filterFrom || filterTo || filterShape || filterAudio,
  );

  useEffect(() => {
    const controller = new AbortController();
    setListLoading(true);
    api.downloads
      .list(
        {
          client_id: filterClient || undefined,
          from: filterFrom || undefined,
          to: filterTo || undefined,
          shape: filterShape || undefined,
          audio_only: filterAudio === '' ? undefined : filterAudio === 'only',
          cursor: pager.cursor,
          limit: pager.pageSize,
        },
        { signal: controller.signal },
      )
      .then((result) => {
        if (controller.signal.aborted) return;
        setPage(result as Page<DownloadRow>);
        setListError(null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setListError(err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setListLoading(false);
      });
    return () => controller.abort();
  }, [
    filterClient,
    filterFrom,
    filterTo,
    filterShape,
    filterAudio,
    pager.cursor,
    pager.pageSize,
    reloadKey,
  ]);

  const clearFilters = () => {
    setFilterClient('');
    setFilterFrom('');
    setFilterTo('');
    setFilterShape('');
    setFilterAudio('');
    pager.reset();
  };

  // --- polling queued runs -------------------------------------------------
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  // Keyed on WHICH runs are still moving, not on the queue itself: polling a status that
  // has not changed still produces a new array, and depending on that would tear down and
  // rebuild the timer on every tick.
  const inFlightKey = queued
    .filter((q) => RUN_IN_FLIGHT.includes(q.status))
    .map((q) => q.run_id)
    .sort()
    .join(',');

  useEffect(() => {
    if (!inFlightKey) return undefined;

    let stop = false;
    const tick = async () => {
      let settled = false;
      for (const runId of inFlightKey.split(',')) {
        try {
          const run = await api.runs.get(runId);
          if (stop) return;
          setQueued((current) =>
            current.map((q) =>
              q.run_id === run.run_id
                ? { ...q, status: run.status, error: run.error, rows_out: run.rows_out }
                : q,
            ),
          );
          if (!RUN_IN_FLIGHT.includes(run.status)) settled = true;
        } catch {
          // A transient read failure is not worth a toast: the next tick retries, and the
          // run row itself is the source of truth either way.
        }
      }
      if (!stop && settled) refreshRef.current();
    };

    const timer = window.setInterval(() => void tick(), POLL_MS);
    return () => {
      stop = true;
      window.clearInterval(timer);
    };
  }, [inFlightKey]);

  // --- selection + artifact detail ----------------------------------------
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /**
   * The detail card is appended after a 50-row table, so on a full page it opens well
   * below the fold. Selecting a row now brings it into view — otherwise the only visible
   * feedback for the click is the row tint, and the natural next move is to click again
   * (which deselects) and conclude the row is dead.
   */
  const detailRef = useRef<HTMLDivElement | null>(null);
  const [selectedRun, setSelectedRun] = useState<Run | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [minting, setMinting] = useState<string | null>(null);

  const selectedItem = useMemo(
    () => page.items.find((i) => i.run_id === selectedId) ?? null,
    [page.items, selectedId],
  );

  /**
   * The run row is the better source when it has loaded — it is what the dispatcher
   * wrote — but the list row carries the same three fields, so the drawer states the
   * coverage from the first paint instead of flickering in a second later. Falling back
   * to the list row also means a failed detail fetch degrades to a true label rather than
   * to no label at all, which is the one degradation this feature cannot accept.
   */
  const selectedCoverage = useMemo(
    () => coverageOf(selectedRun ?? selectedItem),
    [selectedRun, selectedItem],
  );

  useEffect(() => {
    if (!selectedId) return;
    detailRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedRun(null);
      return undefined;
    }
    const controller = new AbortController();
    setSelectedLoading(true);
    api.runs
      .get(selectedId, { signal: controller.signal })
      .then((run) => {
        if (!controller.signal.aborted) setSelectedRun(run);
      })
      .catch(() => {
        // The list row already carries everything the table shows; the detail panel
        // degrades to it rather than erroring the whole screen.
        if (!controller.signal.aborted) setSelectedRun(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setSelectedLoading(false);
      });
    return () => controller.abort();
  }, [selectedId]);

  const download = async (runId: string, artifact: ArtifactRow) => {
    setMinting(artifact.s3_key);
    try {
      const minted = await mintGrant(runId, { s3_key: artifact.s3_key });
      const url = mintedUrl(minted);
      const filename = artifact.filename || basename(artifact.s3_key);
      startDownload(url, filename);
      toast.success('Downloading', {
        duration: 12_000,
        description: `${filename} · the link stays valid until ${istDateTime(minted.expires_at)}`,
        action: {
          label: 'Copy link',
          onClick: () => {
            void copyText(url).then((ok) =>
              ok
                ? toast.success('Link copied')
                : toast.warn('Could not reach the clipboard in this browser'),
            );
          },
        },
      });
    } catch (err) {
      if (isApiError(err, 'no_artifact')) {
        toast.error('Nothing to download yet', { description: errorMessage(err) });
      } else {
        toast.error(errorMessage(err));
      }
    } finally {
      setMinting(null);
    }
  };

  const queuedColumns: Column<Queued>[] = [
    {
      key: 'status',
      header: 'Status',
      width: '116px',
      nowrap: true,
      render: (q) => <StatusBadge status={q.status} />,
    },
    {
      key: 'run',
      header: 'Run',
      width: '230px',
      mono: true,
      render: (q) => (
        <div className="truncate">
          <Link to={`/runs/${encodeURIComponent(q.run_id)}`} title={q.run_id}>
            {q.run_id}
          </Link>
        </div>
      ),
    },
    {
      // Unclamped, wrapping, and immediately after the run it belongs to.
      key: 'why',
      header: 'Why',
      wrap: true,
      render: (q) =>
        q.error ? (
          <span className="small" style={{ color: 'var(--danger)' }}>
            {q.error}
          </span>
        ) : (
          <span className="dim">—</span>
        ),
    },
    {
      key: 'client',
      header: 'Client',
      width: '150px',
      render: (q) => {
        const name = clientById.get(q.client_id)?.name ?? q.client_id;
        return (
          <div className="truncate" title={`${name} · ${q.client_id}`}>
            {name}
          </div>
        );
      },
    },
    {
      key: 'shape',
      header: 'Shape',
      width: '104px',
      nowrap: true,
      render: (q) => (
        <span className="cluster-tight">
          <Badge variant="plain" glyph={q.shape === 'detailed' ? '≡' : '◈'}>
            {q.shape}
          </Badge>
          {q.audio_only ? (
            <Badge variant="plain" glyph="♪">
              audio
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      key: 'rows',
      header: 'Rows',
      width: '96px',
      align: 'right',
      mono: true,
      render: (q) => (q.rows_out ? formatCount(q.rows_out) : <span className="dim">—</span>),
    },
    {
      key: 'requested',
      header: 'Requested (IST)',
      width: '150px',
      nowrap: true,
      optional: true,
      render: (q) => istDateTime(q.requested_at),
    },
  ];

  // --- table ---------------------------------------------------------------
  const columns: Column<DownloadRow>[] = [
    {
      // Rank 1. Status was seventh and rightmost — the first column off the edge at
      // 1280px, on the screen that has to say whether the file is usable.
      key: 'status',
      header: 'Status',
      width: '116px',
      nowrap: true,
      render: (item) =>
        item.status ? <StatusBadge status={item.status} /> : <span className="dim">—</span>,
    },
    {
      key: 'generated',
      header: 'Generated (IST)',
      width: '150px',
      nowrap: true,
      render: (item) => (
        <span title={`${istDateTime(item.generated_at)} · ${timeAgo(item.generated_at)}`}>
          {istDateTime(item.generated_at)}
        </span>
      ),
    },
    {
      key: 'client',
      header: 'Client',
      width: '160px',
      render: (item) => {
        const name =
          item.client_name || clientById.get(item.client_id)?.name || item.client_id;
        return (
          <div className="truncate">
            <Link to={`/clients/${encodeURIComponent(item.client_id)}`} title={`${name} · ${item.client_id}`}>
              {name}
            </Link>
          </div>
        );
      },
    },
    {
      key: 'shape',
      header: 'Shape',
      width: '104px',
      nowrap: true,
      // Classifications, not states. Teal is for things you can click, and a report
      // shape is neither clickable nor ever wrong.
      render: (item) => (
        <span className="cluster-tight">
          <Badge
            variant="plain"
            glyph={item.shape === 'detailed' ? '≡' : '◈'}
            title={item.shape === 'detailed' ? 'One row per call' : 'One row per account'}
          >
            {item.shape}
          </Badge>
          {item.audio_only ? (
            <Badge
              tone="neutral"
              glyph="♪"
              title="Recordings index — one row per call with audio"
            >
              audio
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      // The only width-less column: the coverage sentence is the evidence on this screen
      // and it absorbs every remaining pixel.
      key: 'window',
      header: 'Window (IST)',
      wrap: true,
      headerTitle:
        'For a partial file this is the coverage the source actually had, not the window that was asked for.',
      render: (item) => {
        const coverage = coverageOf(item);
        if (!coverage.isPartial) {
          return (
            <span
              className="small"
              title={item.window_label ? `Requested: ${item.window_label}` : undefined}
            >
              {istDateRange(item.from, item.to)}
            </span>
          );
        }
        return (
          <span className="cluster-tight" style={{ minWidth: 0 }}>
            <CoverageBadge coverage={coverage} />
            <span
              className="small strong"
              title={`Requested ${istDateRange(item.from, item.to)}`}
            >
              {coverageWindowLabel(coverage, item.from, item.to)}
            </span>
          </span>
        );
      },
    },
    {
      key: 'rows',
      header: 'Rows',
      width: '96px',
      align: 'right',
      mono: true,
      headerTitle: 'Rows in the delivered artifact',
      render: (item) => formatCount(item.rows),
    },
    {
      key: 'files',
      header: 'Files',
      width: '146px',
      // Rank 4: the detail card states all of this in full, in words, on selection.
      optional: true,
      render: (item) => {
        const formats = Array.from(
          new Set((item.artifacts || []).map((a) => formatLabel(a.format ?? item.format))),
        );
        return (
          <span className="small nowrap">
            {artifactCount(item)} · {formatBytes(item.bytes)}{' '}
            <span className="xs dim">
              {formats.join(' + ') || '—'}
              {anyFallback(item) ? ' · fell back' : ''}
            </span>
          </span>
        );
      },
    },
    {
      // The screen's whole purpose is to hand back a file, and the only Download button
      // used to be ~1,200px below the row that was clicked — off-screen, so the
      // feedback for clicking a row was invisible.
      key: 'get',
      header: '',
      width: '112px',
      align: 'right',
      nowrap: true,
      render: (item) => {
        const files = item.artifacts || [];
        if (files.length === 0) {
          return (
            <span className="xs dim" title="This run produced no file">
              no file
            </span>
          );
        }
        if (files.length === 1) {
          const only = files[0]!;
          return (
            <Button
              size="sm"
              variant="primary"
              loading={minting === only.s3_key}
              title={`Mint a link and download ${only.filename || basename(only.s3_key)}`}
              onClick={() => void download(item.run_id, only)}
            >
              Download
            </Button>
          );
        }
        return (
          <Button
            size="sm"
            title="Several files in this run — open the list below"
            onClick={() => setSelectedId(item.run_id)}
          >
            {files.length} files ▸
          </Button>
        );
      },
    },
  ];

  // ---------------------------------------------------------------------------

  return (
    <div className="stack">
      <PageHeader
        title="Downloads"
        subtitle="Generate a report on demand for any client, window and shape — then pull it through a link that expires in 7 days like every other. All dates are IST."
        actions={
          <Button onClick={refresh} loading={listLoading} icon="↻">
            Refresh
          </Button>
        }
      />

      {clientsError ? (
        <div className="callout tone-danger">
          <span className="callout-glyph" aria-hidden="true">
            ✕
          </span>
          <span>Could not load clients: {errorMessage(clientsError)}</span>
        </div>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      <Card
        title="Generate a download"
        actions={
          <Button variant="primary" onClick={() => void submit()} loading={submitting} disabled={!canSubmit}>
            Generate
          </Button>
        }
      >
        <div className="stack">
          <div className="field-row" style={CAPPED_FIELDS}>
            <Field
              label="Client"
              required
              hint={
                client
                  ? `Workspace ${client.company_id ?? 'not set'} · ${client.company_name}`
                  : 'Reports are keyed on company_id, not on the S3 prefix.'
              }
            >
              {(p) => (
                <Select
                  {...p}
                  value={clientId}
                  disabled={clientsLoading || clients.length === 0}
                  onChange={(event) => setClientId(event.target.value)}
                >
                  {clients.length === 0 ? <option value="">No clients</option> : null}
                  {clients.map((c) => (
                    <option key={c.client_id} value={c.client_id}>
                      {c.name}
                      {c.active ? '' : ' (inactive)'}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="Shape"
              required
              hint={
                shape === 'detailed'
                  ? `One row per call. Maximum ${DETAILED_MAX_WINDOW_DAYS} days.`
                  : 'One row per account, rolled up across every attempt.'
              }
            >
              {(p) => (
                <Select
                  {...p}
                  value={shape}
                  onChange={(event) => setShape(event.target.value as ReportShape)}
                >
                  {REPORT_SHAPES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label} — {s.hint}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="Format"
              hint={`XLSX over ${formatCount(XLSX_ROW_THRESHOLD)} rows spills to gzipped CSV rather than truncating.`}
            >
              {(p) => (
                <Select
                  {...p}
                  value={format}
                  onChange={(event) => setFormat(event.target.value as ReportFormat)}
                >
                  <option value="xlsx">XLSX (Excel)</option>
                  <option value="csv">CSV, gzipped</option>
                </Select>
              )}
            </Field>
          </div>

          <div className="field-row" style={CAPPED_FIELDS}>
            <Field label="Window" required hint="Resolved in Asia/Kolkata at generation time.">
              {(p) => (
                <Select
                  {...p}
                  value={draft.type}
                  onChange={(event) =>
                    setDraft((d) => ({ ...d, type: event.target.value as WindowType }))
                  }
                >
                  {WINDOW_TYPES.map((w) => (
                    <option key={w.value} value={w.value}>
                      {w.label} — {w.hint}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            {draft.type === 'last_n_days' ? (
              <Field label="Days" required error={built.error} hint="Inclusive, ending yesterday.">
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    min={1}
                    max={366}
                    value={draft.n}
                    onChange={(event) => setDraft((d) => ({ ...d, n: Number(event.target.value) }))}
                  />
                )}
              </Field>
            ) : null}

            {draft.type === 'from_date' || draft.type === 'custom' ? (
              <Field label="From" required error={built.error} hint="IST calendar date.">
                {(p) => (
                  <Input
                    {...p}
                    type="date"
                    max={istToday()}
                    value={draft.from}
                    onChange={(event) => setDraft((d) => ({ ...d, from: event.target.value }))}
                  />
                )}
              </Field>
            ) : null}

            {draft.type === 'custom' ? (
              <Field label="To" required hint="Inclusive.">
                {(p) => (
                  <Input
                    {...p}
                    type="date"
                    max={istToday()}
                    value={draft.to}
                    onChange={(event) => setDraft((d) => ({ ...d, to: event.target.value }))}
                  />
                )}
              </Field>
            ) : null}
          </div>

          {/* A value read back to the operator is not an alert and does not get a box.
              The standing paragraph below it never changes with the input, so it is a
              disclosure rather than 90 words re-read on every visit. Its words are
              unchanged: it states what this screen does and does not accept. */}
          {built.window && resolved ? (
            <div className="readback">
              Reads{' '}
              <span className="strong num">{istDateRange(resolved.from, resolved.to)}</span> ·{' '}
              <span className="num">
                {resolved.days} day{resolved.days === 1 ? '' : 's'}
              </span>{' '}
              (IST).{' '}
              <details style={{ display: 'inline' }}>
                <summary className="xs dim" style={{ cursor: 'pointer', display: 'inline' }}>
                  Why a part day is not a partial read
                </summary>
                <span>
                  {' '}
                  Source objects are read whole — a partial READ would give every borrower with
                  only their earliest calls. That is a different thing from a partial DAY, which
                  is the file itself stopping early: the export is produced by hand and may be
                  run before the day&apos;s calling has finished. This screen accepts one and
                  labels it; it never truncates a read.
                </span>
              </details>
            </div>
          ) : null}

          {guardrail ? (
            <div className="callout tone-danger" role="alert">
              <span className="callout-glyph" aria-hidden="true">
                ✕
              </span>
              <span>{guardrail}</span>
            </div>
          ) : null}

          <div className="field-row" style={CAPPED_FIELDS}>
            <Field
              label="Flow"
              optional
              hint="Exact match on the source flow column. Blank means every flow."
            >
              {(p) => (
                <Input
                  {...p}
                  value={flow}
                  placeholder="e.g. collections_hindi"
                  onChange={(event) => setFlow(event.target.value)}
                />
              )}
            </Field>
            <Field
              label="Campaign pattern"
              optional
              hint="Substring or glob against campaign_name."
            >
              {(p) => (
                <Input
                  {...p}
                  value={campaign}
                  placeholder="e.g. HDB_SEP_*"
                  onChange={(event) => setCampaign(event.target.value)}
                />
              )}
            </Field>
          </div>

          <div className="stack-tight">
            <Switch
              checked={audioOnly}
              disabled={Boolean(audioBlockedReason)}
              onChange={setAudioOnly}
              label="Calls with audio only — build the recordings index"
            />
            {audioBlockedReason ? (
              <p className="small muted">{audioBlockedReason}</p>
            ) : (
              <details>
                <summary className="small muted" style={{ cursor: 'pointer' }}>
                  What the recordings index contains, and why it counts as an opt-in selection
                </summary>
                <p className="small muted" style={{ marginTop: 'var(--s-1)' }}>
                  One row per call that actually has a recording: account, name, phone, language,
                  called at, disposition, duration and a <span className="mono">/r/&#123;token&#125;</span>{' '}
                  link the resolver re-signs for 60 seconds at click time. A raw Azure SAS never
                  goes into a delivered file. A recording link is opt-in content, so this counts as
                  an explicit selection of{' '}
                  <span className="mono">{String(recordingColumn ?? 'the recording column')}</span>{' '}
                  and is recorded as one on the audit row.
                </p>
              </details>
            )}
          </div>

          {optInColumns.length > 0 ? (
            <div className="stack-tight">
              <div className="cluster-tight">
                <span className="eyebrow">Opt-in columns</span>
                <PolicyBadge tier="opt_in" />
                <span className="grow" />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setOptIn(optIn.length === optInColumns.length ? [] : [...optInColumns])}
                >
                  {optIn.length === optInColumns.length ? 'Clear all' : 'Select all'}
                </Button>
              </div>
              <p className="small muted">
                Anything derived from what was said on the call ships only when it is selected
                here, and is suppressed again at emit time on rows where the borrower was not the
                counterparty.
                {client?.reached_values
                  ? ` Reached means ${Object.entries(client.reached_values)
                      .map(([k, v]) => `${k} in [${(v ?? []).join(', ')}]`)
                      .join('; ')}.`
                  : ''}
              </p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
                  gap: 'var(--s-1) var(--s-3)',
                }}
              >
                {optInColumns.map((column) => (
                  <Checkbox
                    key={column}
                    label={column}
                    checked={optIn.includes(column)}
                    onChange={() => toggleOptIn(column)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {neverColumns.length > 0 ? (
            <details>
              <summary className="small muted" style={{ cursor: 'pointer' }}>
                {neverColumns.length} column{neverColumns.length === 1 ? '' : 's'} can never be
                released for this client
              </summary>
              <div className="cluster-tight" style={{ marginTop: 'var(--s-2)' }}>
                <PolicyBadge tier="never" />
                <span className="small mono dim">{neverColumns.join(', ')}</span>
              </div>
              <p className="small muted" style={{ marginTop: 'var(--s-1)' }}>
                Conduct-QA fields are our own agent&apos;s defect record, not a client
                deliverable. No selection on this screen can reach them.
              </p>
            </details>
          ) : null}
        </div>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {queued.length > 0 ? (
        <Card
          title="This session's requests"
          flush
          footer={
            <span className="xs dim">
              A download run is generated and held behind a grant — it is never mailed to the
              client&apos;s recipients.
            </span>
          }
        >
          {/* A table, not a wrapping flex cluster: nothing used to align between rows, and
              the failure reason landed last — after a `.grow` spacer and a timestamp — on
              the card whose whole job is saying what happened to the thing you just asked
              for. The run id is shown whole; 22 characters is not a ULID, and this is the
              correlation key that goes into a ticket. */}
          <Table<Queued>
            columns={queuedColumns}
            rows={queued}
            rowKey={(q) => q.run_id}
            dense
            flush
            rowTone={(q) =>
              q.status === 'failed' ? 'danger' : q.status === 'stale' ? 'warn' : 'default'
            }
            caption="Downloads requested in this browser session"
          />
        </Card>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      <Card
        title="Generated files"
        actions={
          filtersActive ? (
            <Button size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          ) : undefined
        }
        flush
      >
        <div className="toolbar" style={{ border: 0, borderRadius: 0 }}>
          <Select
            size="sm"
            value={filterClient}
            aria-label="Filter by client"
            style={{ maxWidth: '200px' }}
            onChange={(event) => {
              setFilterClient(event.target.value);
              pager.reset();
            }}
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.client_id} value={c.client_id}>
                {c.name}
              </option>
            ))}
          </Select>

          <Select
            size="sm"
            value={filterShape}
            aria-label="Filter by shape"
            style={{ maxWidth: '160px' }}
            onChange={(event) => {
              setFilterShape(event.target.value as '' | ReportShape);
              pager.reset();
            }}
          >
            <option value="">Any shape</option>
            <option value="borrower">Borrower</option>
            <option value="detailed">Detailed</option>
          </Select>

          <Select
            size="sm"
            value={filterAudio}
            aria-label="Filter by recordings index"
            style={{ maxWidth: '230px' }}
            onChange={(event) => {
              setFilterAudio(event.target.value as '' | 'only' | 'exclude');
              pager.reset();
            }}
          >
            <option value="">Reports and audio indexes</option>
            <option value="only">Recordings index only</option>
            <option value="exclude">Exclude recordings indexes</option>
          </Select>

          <label className="cluster-tight small">
            <span className="dim">Window from (IST)</span>
            <Input
              size="sm"
              type="date"
              value={filterFrom}
              aria-label="Window starts on or after"
              style={{ maxWidth: '150px' }}
              onChange={(event) => {
                setFilterFrom(event.target.value);
                pager.reset();
              }}
            />
          </label>
          <label className="cluster-tight small">
            <span className="dim">Window to (IST)</span>
            <Input
              size="sm"
              type="date"
              value={filterTo}
              aria-label="Window ends on or before"
              style={{ maxWidth: '150px' }}
              onChange={(event) => {
                setFilterTo(event.target.value);
                pager.reset();
              }}
            />
          </label>
        </div>

        <Table<DownloadRow>
          columns={columns}
          rows={page.items}
          rowKey={(item) => item.run_id}
          loading={listLoading}
          dense
          flush
          onRowClick={(item) => setSelectedId(item.run_id === selectedId ? null : item.run_id)}
          isRowActive={(item) => item.run_id === selectedId}
          rowTone={(item) =>
            item.status === 'failed'
              ? 'danger'
              : item.status === 'stale'
                ? 'warn'
                : coverageOf(item).isPartial
                  ? 'partial'
                  : anyFallback(item)
                    ? 'warn'
                    : 'default'
          }
          empty={
            listError ? (
              <EmptyState
                tone="danger"
                title="Could not load downloads"
                description={errorMessage(listError)}
                action={
                  <Button variant="primary" onClick={refresh}>
                    Retry
                  </Button>
                }
              />
            ) : filtersActive ? (
              <EmptyState
                tone="filtered"
                title="Nothing matches those filters"
                description="No generated file overlaps that client, shape and date range."
                action={
                  <Button variant="primary" onClick={clearFilters}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="Nothing generated yet"
                description="Use the form above to pull a report for any client and window. It does not have to be on a schedule."
              />
            )
          }
          footer={
            <Pagination
              page={pager.page}
              pageSize={pager.pageSize}
              count={page.items.length}
              hasNext={Boolean(page.cursor)}
              hasPrev={pager.hasPrev}
              onNext={() => pager.next(page.cursor)}
              onPrev={pager.prev}
              onPageSize={pager.setPageSize}
              loading={listLoading}
              label="downloads"
            />
          }
        />
      </Card>

      {/* ------------------------------------------------------------------ */}
      <div ref={detailRef}>
      {selectedItem ? (
        <Card
          title={
            <span className="cluster-tight">
              <span>Files in this run</span>
              <span className="mono small dim">{selectedItem.run_id}</span>
            </span>
          }
          actions={
            <>
              <Link className="btn btn-sm" style={{ textDecoration: 'none' }} to={`/runs/${encodeURIComponent(selectedItem.run_id)}`}>
                Open run
              </Link>
              <Button size="sm" variant="ghost" onClick={() => setSelectedId(null)}>
                Close
              </Button>
            </>
          }
        >
          <div className="stack">
            <dl className="kv">
              <dt>Client</dt>
              <dd>
                {selectedItem.client_name || clientById.get(selectedItem.client_id)?.name}{' '}
                <span className="dim mono small">{selectedItem.client_id}</span>
              </dd>
              <dt>Window (IST)</dt>
              <dd>
                {istDateRange(selectedItem.from, selectedItem.to)}
                {selectedItem.window_label ? (
                  <span className="dim"> · {selectedItem.window_label}</span>
                ) : null}
              </dd>
              <dt>Coverage (IST)</dt>
              <dd>
                {selectedCoverage.isPartial ? (
                  <div className="stack-tight">
                    <span className="strong">
                      {coverageWindowLabel(selectedCoverage, selectedItem.from, selectedItem.to)}
                    </span>
                    <span className="xs dim">
                      Derived from the latest dialed time in the data, not from when the export
                      file was written — the file is written later than its contents and
                      overstates them.
                    </span>
                  </div>
                ) : selectedCoverage.measured ? (
                  <span>
                    Full window · last call in the source{' '}
                    <span className="strong">{selectedCoverage.coveredToLabel}</span>
                  </span>
                ) : (
                  <span className="dim">
                    Not measured — no covered-to time was recorded for this run
                  </span>
                )}
              </dd>
              <dt>Generated</dt>
              <dd>{istDateTime(selectedItem.generated_at)}</dd>
              <dt>Rows</dt>
              <dd className="mono">
                {selectedRun
                  ? `${formatCount(selectedRun.rows_in)} read → ${formatCount(selectedRun.rows_out)} out`
                  : formatCount(selectedItem.rows)}
              </dd>
              {selectedRun?.source_keys?.length ? (
                <>
                  <dt>Source</dt>
                  <dd className="mono small">
                    {selectedRun.source_keys.map((key) => (
                      <div key={key} style={{ overflowWrap: 'anywhere' }}>
                        {key}
                      </div>
                    ))}
                  </dd>
                </>
              ) : null}
              {selectedRun?.columns?.length ? (
                <>
                  <dt>Columns</dt>
                  <dd>
                    <details>
                      <summary style={{ cursor: 'pointer' }}>
                        {selectedRun.columns.length} column
                        {selectedRun.columns.length === 1 ? '' : 's'} emitted
                      </summary>
                      <div className="code-block" style={{ marginTop: 'var(--s-2)', whiteSpace: 'pre-wrap' }}>
                        {selectedRun.columns.join('\n')}
                      </div>
                    </details>
                  </dd>
                </>
              ) : null}
              {selectedRun?.unranked_dispositions?.length ? (
                <>
                  <dt>Unranked</dt>
                  <dd>
                    <div className="cluster-tight">
                      {selectedRun.unranked_dispositions.map((value) => (
                        <Badge key={value} tone="warn" glyph="?" title="Not in this client's rank list">
                          {value}
                        </Badge>
                      ))}
                    </div>
                    <div className="xs dim" style={{ marginTop: 'var(--s-1)' }}>
                      Shown exactly as the source wrote them.{' '}
                      <Link to={`/dispositions/${encodeURIComponent(selectedItem.client_id)}`}>
                        Rank them
                      </Link>
                      .
                    </div>
                  </dd>
                </>
              ) : null}
            </dl>

            {selectedCoverage.isPartial ? (
              <div className="callout tone-warn" role="alert">
                <span className="callout-glyph" aria-hidden="true">
                  ◧
                </span>
                <div className="stack-tight">
                  <div className="strong">
                    This file is a part day. Do not forward it as a whole one.
                  </div>
                  <div>
                    {selectedCoverage.coveredTo ? (
                      <>
                        The latest call in the source is{' '}
                        <span className="strong">{selectedCoverage.coveredToLabel}</span>, while
                        the file claims{' '}
                        {istDateRange(selectedItem.from, selectedItem.to)}. Nothing was mailed —
                        a partial source is accepted for an internal download because the file
                        does not leave the building. It leaves when you send it, and the
                        filename still carries the requested dates, so state the coverage above
                        when you do.
                      </>
                    ) : (
                      <>
                        The source stops before the window closes, and no coverage time was
                        recorded for this run. Nothing was mailed.
                      </>
                    )}
                  </div>
                  {selectedCoverage.rowsMisaligned !== null && selectedCoverage.rowsMisaligned > 0 ? (
                    <div>{misalignedNote(selectedCoverage)}</div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {selectedLoading ? <p className="small dim">Loading run detail…</p> : null}

            {(selectedItem.artifacts || []).length === 0 ? (
              <EmptyState
                title="This run produced no file"
                description={
                  selectedItem.status === 'stale'
                    ? 'The source object was missing or older than the window, so nothing was generated and nothing was sent.'
                    : 'Nothing was written for this run.'
                }
              />
            ) : (
              <div className="stack">
                {(selectedItem.artifacts || []).map((artifact) => {
                  const fallback = fellBack(artifact, selectedItem.format);
                  return (
                    <div
                      key={artifact.s3_key}
                      style={{
                        // An EVEN 1px border, tinted by tone — the way .toast solves this.
                        // A 3px left edge over a tinted fill is the banned accent-bar shape,
                        // and spelling it `borderLeftWidth` in JSX was the only reason the
                        // guardrail could not see it.
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        borderColor: fallback ? 'var(--warn-line)' : 'var(--line)',
                        borderRadius: 'var(--r-2)',
                        padding: 'var(--s-3)',
                      }}
                    >
                      <div className="cluster">
                        <Badge
                          tone={artifact.format === 'xlsx' ? 'neutral' : 'info'}
                          glyph={artifact.format === 'xlsx' ? '▦' : '⇩'}
                          title={
                            artifact.format === 'xlsx'
                              ? 'Excel workbook'
                              : 'Gzipped CSV — decompress before opening in Excel'
                          }
                        >
                          {formatLabel(artifact.format ?? selectedItem.format)}
                        </Badge>
                        <span className="strong">
                          {artifact.label || artifact.kind}
                        </span>
                        {/* Beside the button, not in the drawer header: this is the last
                            thing read before the click that puts the file on a desktop. */}
                        <CoverageBadge coverage={selectedCoverage} />
                        <MisalignedBadge coverage={selectedCoverage} />
                        <span className="grow" />
                        <Button
                          size="sm"
                          variant="primary"
                          loading={minting === artifact.s3_key}
                          onClick={() => void download(selectedItem.run_id, artifact)}
                        >
                          Download
                        </Button>
                      </div>

                      <div className="cluster small" style={{ marginTop: 'var(--s-2)' }}>
                        <span className="mono">{formatCount(artifact.rows)} rows</span>
                        <span className="dim">·</span>
                        <span className="mono">{formatBytes(artifact.bytes)}</span>
                        {artifact.columns ? (
                          <>
                            <span className="dim">·</span>
                            <span className="mono">{formatCount(artifact.columns)} columns</span>
                          </>
                        ) : null}
                      </div>

                      <div
                        className="xs dim mono"
                        style={{ marginTop: 'var(--s-1)', overflowWrap: 'anywhere' }}
                      >
                        {artifact.s3_key}
                      </div>

                      {fallback ? (
                        <div className="callout tone-warn" style={{ marginTop: 'var(--s-2)' }}>
                          <span className="callout-glyph" aria-hidden="true">
                            ▲
                          </span>
                          <span>
                            <strong>XLSX was requested; gzipped CSV was delivered.</strong>{' '}
                            {fallbackReason(artifact)} The file is complete — decompress it
                            (gunzip or 7-Zip) before opening it in Excel.
                          </span>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}

            <p className="small muted">
              Downloading mints a grant in your name: the same 7-day window, the same revoke
              switch and the same per-click trail a client&apos;s link has. The object itself is
              never presigned for longer than 60 seconds.
            </p>
          </div>
        </Card>
      ) : null}

      {selectedItem ? (
        <Card title="Links issued for this run">
          <GrantList
            runId={selectedItem.run_id}
            sortBy="expiry"
            emptyTitle="No links yet"
            emptyDescription="Nobody has downloaded this file and it was not mailed out. Use Download above to mint one."
          />
        </Card>
      ) : null}
      </div>

      <p className="xs dim">
        Source objects are read from the Neuron export bucket read-only; every artifact on this
        screen lives in the dispatch stack&apos;s own bucket. Expiring a link never deletes a
        client&apos;s data — file retention is a separate setting. Times shown in IST.
      </p>
    </div>
  );
}
